import { useCallback } from 'react';
import { identity } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { LeaseDealsModel, LeaseDealsPaths } from 'client/data/models/lease-deals';
import {
  FILTER_QUERY_PARAMS,
  SORT_BY_CONFIG,
  DEFAULT_SORT_PARAM,
} from 'site-modules/shared/constants/lease-deals/lease-deals';
import { maybe } from 'site-modules/shared/utils/incentives/gql-incentives-filter';
import { useUrlFilterSync } from 'site-modules/lease-deals/hooks/use-url-filter-sync/use-url-filter-sync';
import { numberWithCommasV2 } from 'site-modules/shared/utils/string';

const ENGINE_TYPE_VALUE_TO_LABEL_MAP = {
  diesel: 'Diesel',
  electric: 'Electric',
  hybrid: 'Hybrid',
  'mild hybrid': 'Mild Hybrid',
  'plug-in hybrid': 'Plug-In Hybrid',
  gas: 'Gas',
  'natural gas': 'Natural Gas',
  'flex-fuel': 'Flex-Fuel',
};

const transformNumericFacet = labelFormatter => options =>
  options
    .filter(({ value }) => value !== null && value !== undefined)
    .map(({ value, count }) => ({ label: labelFormatter(value), value, count }))
    .toSorted((a, b) => Number(a.value) - Number(b.value));

const transformStrategyMap = {
  [FILTER_QUERY_PARAMS.BODY_TYPE]: options => options.map(({ value, count }) => ({ label: value, value, count })),
  [FILTER_QUERY_PARAMS.ENGINE_TYPE]: options =>
    options.map(({ value, count }) => ({ label: ENGINE_TYPE_VALUE_TO_LABEL_MAP[value] || value, value, count })),
  [FILTER_QUERY_PARAMS.MAKE]: options =>
    options
      .map(({ value, count }) => ({ label: value, value, count }))
      .toSorted((a, b) => a.value.localeCompare(b.value)),
  [FILTER_QUERY_PARAMS.DEALS]: options => options.map(opt => ({ label: opt, value: opt })),
  [FILTER_QUERY_PARAMS.MONTHLY_PAYMENT]: ({ min, max }) => [min, max],
  [FILTER_QUERY_PARAMS.DUE_AT_SIGNING]: ({ min, max }) => [min, max],
  [FILTER_QUERY_PARAMS.TERM]: transformNumericFacet(value => `${value} mos`),
  [FILTER_QUERY_PARAMS.MILEAGE_ALLOWANCE]: transformNumericFacet(value => `${numberWithCommasV2(Number(value))} mi/yr`),
  [FILTER_QUERY_PARAMS.TOTAL_SEATING]: transformNumericFacet(value => `${value} seats`),
  [FILTER_QUERY_PARAMS.SORT_BY]: options =>
    options.map(opt => ({ text: SORT_BY_CONFIG[opt].label, title: opt, hideValue: true })),
};

const transformFilters = initialFilters => {
  return Object.values(FILTER_QUERY_PARAMS).reduce((acc, filterKey) => {
    const transformDataFunction = transformStrategyMap[filterKey] || identity;
    return { ...acc, [filterKey]: maybe(initialFilters?.[filterKey]).map(transformDataFunction).unpack() };
  }, {});
};

const FiltersDataProviderUI = ({ filters, setModelValue, children }) => {
  const filtersData = transformFilters(filters);
  const onUpdate = useUrlFilterSync(setModelValue);

  const onFilterUpdate = useCallback(
    (filterKey, newValues) => {
      onUpdate({
        [filterKey]: newValues,
        pageNum: [1],
        ...(filterKey === FILTER_QUERY_PARAMS.DEALS ? { sortBy: [DEFAULT_SORT_PARAM] } : {}),
      });
    },
    [onUpdate]
  );

  return children(filtersData, onFilterUpdate);
};

const filtersPageBindings = {
  filters: bindToPath(LeaseDealsPaths.getFiltersPath(), LeaseDealsModel, null, false),
};

export const FiltersDataProvider = connectToModel(FiltersDataProviderUI, filtersPageBindings);
FiltersDataProvider.displayName = 'FiltersDataProvider';
